import moment from "moment";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import View from "../../assets/image/view.svg";
import { Row, Col, Button, ButtonGroup } from "react-bootstrap";
import { useGetEventBookingByUserMutation } from "../../Services/api";
import Pagination from "../../Components/Pagination/Pagination";
import Spinnerr from "../../Components/Spinner/Spinner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { downloadTicket } from "../../Utils/ticketDownloads";

const EventUserBookingList = ({ id, eventData }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [searchQuery, setSearchQuery] = useState({
    page: 1,
    limit: 10,
    event_id: id,
  });
  const [page, setPage] = useState(1); // Initialize page state to start from 0
  const [totalPages, setTotalPages] = useState(1);
  const [nextPage, setNextPage] = useState(null);

  const [bookingList, response] = useGetEventBookingByUserMutation();
  useEffect(() => {
    bookingList(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);
  useEffect(() => {
    if (!response.isLoading && response.isSuccess) {
      setLoading(false);
      setData(response?.data?.data);
      setPage(response?.data?.data?.page);
      setTotalPages(response?.data?.data?.total_pages);
      setNextPage(response?.data?.data?.next_page);
    } else if (response.isError) {
      console.log(response?.error);
      setLoading(false);
      setError(true);
    }
  }, [response]);

  const exportToPDF = async () => {
    try {
      // Show loading state
      setLoading(true);

      // Get all data without pagination
      const allDataResponse = await bookingList({
        page: 1,
        limit: 1000, // Large limit to get all data
        event_id: id,
      });

      if (allDataResponse.data?.data?.event_list) {
        const allBookings = allDataResponse.data.data.event_list;

        const doc = new jsPDF();

        // Add title
        doc.setFontSize(16);
        doc.text("User Bookings Report", 14, 22);
        doc.setFontSize(10);
        doc.text(`Generated on: ${moment().format("MM/DD/YYYY HH:mm")}`, 14, 30);
        doc.text(`Total Records: ${allBookings.length}`, 14, 38);

        // Prepare table data
        const tableData = allBookings.map((booking, index) => [
          index + 1,
          booking.user_detail?.first_name || "-",
          booking.user_detail?.phone || "-",
          moment(booking.createdAt).format("MM/DD/YYYY"),
          Array.isArray(booking.beverage)
            ? booking.beverage.reduce((total, item) => total + (Number(item?.total_price) || 0), 0)
            : "-",
          booking.tax || "-",
          Array.isArray(booking.beverage) && booking.beverage.length > 0
            ? booking.beverage.map(item => item?.ticket_name || "Beverage").join(", ")
            : "-",
          Array.isArray(booking.beverage) && booking.beverage.length > 0
            ? booking.beverage.map(item => item?.selected_count || 0).join(", ")
            : "-",
          Array.isArray(booking.beverage) && booking.beverage.length > 0
            ? booking.beverage.map(item => item?.total_price || 0).join(", ")
            : "-"
        ]);

        // Add table
        autoTable(doc, {
          head: [["Sr.No.", "User Name", "Phone", "Date", "Total Amount", "Tax", "Ticket Name", "Quantity", "Amount"]],
          body: tableData,
          startY: 50,
          styles: {
            fontSize: 8,
            cellPadding: 2
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255
          }
        });

        // Save PDF
        doc.save(`user-bookings-${moment().format("YYYY-MM-DD")}.pdf`);
      }
    } catch (error) {
      console.error("PDF export error:", error);
    } finally {
      // Restore original data and loading state
      setLoading(false);
      bookingList(searchQuery);
    }
  };

  const exportToExcel = async () => {
    try {
      // Show loading state
      setLoading(true);

      // Get all data without pagination
      const allDataResponse = await bookingList({
        page: 1,
        limit: 1000, // Large limit to get all data
        event_id: id,
      });

      if (allDataResponse.data?.data?.event_list) {
        const allBookings = allDataResponse.data.data.event_list;

        // Prepare data for Excel
        const excelData = allBookings.map((booking, index) => ({
          "Sr.No.": index + 1,
          "User Name": booking.user_detail?.first_name || "-",
          "Phone": booking.user_detail?.phone || "-",
          "Date of Bookings": moment(booking.createdAt).format("MM/DD/YYYY"),
          "Total Amount": Array.isArray(booking.beverage)
            ? booking.beverage.reduce((total, item) => total + (Number(item?.total_price) || 0), 0)
            : "-",
          "Tax": booking.tax || "-",
          "Ticket Name": Array.isArray(booking.beverage) && booking.beverage.length > 0
            ? booking.beverage.map(item => item?.ticket_name || "Beverage").join(", ")
            : "-",
          "Quantity": Array.isArray(booking.beverage) && booking.beverage.length > 0
            ? booking.beverage.map(item => item?.selected_count || 0).join(", ")
            : "-",
          "Amount": Array.isArray(booking.beverage) && booking.beverage.length > 0
            ? booking.beverage.map(item => item?.total_price || 0).join(", ")
            : "-"
        }));

        // Create workbook and worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "User Bookings");

        // Auto-size columns
        const colWidths = [
          { wch: 8 },  // Sr.No.
          { wch: 20 }, // User Name
          { wch: 15 }, // Phone
          { wch: 15 }, // Date
          { wch: 15 }, // Total Amount
          { wch: 10 }, // Tax
          { wch: 30 }, // Ticket Name
          { wch: 15 }, // Quantity
          { wch: 15 }  // Amount
        ];
        ws['!cols'] = colWidths;

        // Save Excel file
        XLSX.writeFile(wb, `user-bookings-${moment().format("YYYY-MM-DD")}.xlsx`);
      }
    } catch (error) {
      console.error("Excel export error:", error);
    } finally {
      // Restore original data and loading state
      setLoading(false);
      bookingList(searchQuery);
    }
  };

  // Helper function to calculate total tickets
  const getTotalTickets = (booking) => {
    if (Array.isArray(booking.beverage) && booking.beverage.length > 0) {
      return booking.beverage.reduce(
        (total, item) => total + (Number(item?.selected_count) || 0),
        0
      );
    }
    return 0;
  };

  // Handler to download ticket for a single booking
  const handleDownloadTicket = async (booking) => {
    try {
      const ticketData = {
        eventName: eventData?.event_name || booking.event_detail?.event_name || "Event",
        eventImage: eventData?.event_images?.[0]?.image || booking.event_images?.[0]?.image || booking.event_detail?.event_images?.[0]?.image,
        location: eventData?.location || booking.event_detail?.location || "-",
        dateTime: eventData?.date_time || booking.event_detail?.date_time || Math.floor(Date.now() / 1000),
        bookingId: booking.booking_id || booking._id || "-",
        ticketCount: getTotalTickets(booking),
        amount: booking.beverage.reduce((total, item) => total + (Number(item?.total_price) || 0), 0),
        userName: booking.user_detail?.first_name || "-",
        userPhone: booking.user_detail?.phone || "",
        organizerName: booking.organizer_detail?.organizer_name,
        organizerLogo: booking.organizer_detail?.image,
        ticketName: booking.beverage.map(item => item?.ticket_name || "Beverage").join(", "),
      };

      await downloadTicket(ticketData);
    } catch (error) {
      console.error("Error downloading ticket:", error);
    }
  };

  return (
    <>
      {loading ? (
        <Spinnerr />
      ) : error ? (
        <h2 className="my-4">{response?.error?.data?.message}</h2>
      ) : (
        <>
          <h2 className="my-4">Booking From Users</h2>

          {/* Export Buttons */}
          {data?.event_list && data.event_list.length > 0 && (
            <div className="mb-3 d-flex justify-content-end">
              <ButtonGroup>
                <Button
                  onClick={exportToPDF}
                  className="me-2"
                  style={{
                    backgroundColor: '#000000',
                    borderColor: '#000000',
                    color: '#ffffff'
                  }}
                >
                  <i className="fas fa-file-pdf me-2"></i>
                  Export to PDF
                </Button>
                <Button
                  onClick={exportToExcel}
                  style={{
                    backgroundColor: '#000000',
                    borderColor: '#000000',
                    color: '#ffffff'
                  }}
                >
                  <i className="fas fa-file-excel me-2"></i>
                  Export to Excel
                </Button>
              </ButtonGroup>
            </div>
          )}

          <div className="common-table-wrapper m-0 mt-5">
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Sr.No.</th>
                    <th>User Name</th>
                    <th>Phone</th>
                    <th>Date of Bookings</th>
                    <th>Total Amount</th>
                    <th>Tax</th>
                    <th>Ticket Name</th>
                    <th>Quantity</th>
                    <th>Amount</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.event_list.length > 0 ? (
                    data.event_list.map(
                      (booking, i) => {
                        const { user_detail, createdAt, tax, beverage } = booking;
                        return (
                          <tr key={i}>
                            <td>{(page - 1) * 10 + i + 1}</td>
                            <td>{user_detail?.first_name || "-"}</td>
                            <td>{user_detail?.phone || "-"}</td>
                            <td>
                              {moment(createdAt).format("MM/DD/YYYY") || "-"}
                            </td>
                            <td>
                              {Array.isArray(beverage)
                                ? beverage.reduce(
                                  (total, item) =>
                                    total + (Number(item?.total_price) || 0),
                                  0
                                )
                                : "-"}
                            </td>
                            <td>{tax || "-"}</td>
                            <td>
                              {Array.isArray(beverage) && beverage.length > 0 ? (
                                beverage.map((item, idx) => (
                                  <div key={idx}>
                                    {item?.ticket_name || "Beverage"}
                                  </div>
                                ))
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              {Array.isArray(beverage) && beverage.length > 0 ? (
                                beverage.map((item, idx) => (
                                  <div key={idx}>{item?.selected_count || 0}</div>
                                ))
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              {Array.isArray(beverage) && beverage.length > 0 ? (
                                beverage.map((item, idx) => (
                                  <div key={idx}>{item?.total_price || 0}</div>
                                ))
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleDownloadTicket(booking)}
                                style={{
                                  borderColor: '#000000',
                                  color: '#000000',
                                  backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#000000';
                                  e.target.style.color = '#ffffff';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = '#000000';
                                }}
                              >
                                <i className="fas fa-download me-1"></i>
                                Download
                              </Button>
                            </td>
                          </tr>
                        );
                      }
                    )
                  ) : (
                    <tr>
                      <td className="text-center" colSpan={10}>
                        No Records Founds
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {data?.event_list.length > 0 ? (
              <Row className="mt-4 px-2">
                <Col md={5}>
                  <div className="dataTables_info">
                    {`Showing ${Math.min((page - 1) * 10 + 1, data?.total)}` +
                      ` to ${Math.min(page * 10, data?.total) || 0} of ${data?.total || 0
                      } entries`}
                  </div>
                </Col>
                <Col md={7}>
                  <Pagination
                    total={totalPages}
                    setPage={(newPage) => {
                      setSearchQuery((prevQuery) => ({
                        ...prevQuery,
                        page: newPage,
                      }));
                    }}
                    page={page} // Add 1 to page state to show correct page number
                    nextPage={nextPage} // Pass the next page number to the Pagination component
                  />
                </Col>
              </Row>
            ) : (
              ""
            )}
          </div>
        </>
      )}
    </>
  );
};

export default EventUserBookingList;
